import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createQuotation, updateQuotation } from '../api/quotationService';


function QuotationForm({ initialData, onClose, onAddQuotation }) {
    const [date, setDate] = useState("");
    const [customerFullName, setCustomerFullName] = useState("");
    const [sanctionCapacity, setSanctionCapacity] = useState("");
    const [amount, setAmount] = useState("");
    const [moduleMake, setModuleMake] = useState("");
    const [moduleCount, setModuleCount] = useState("");
    const [wattagePerModule, setWattagePerModule] = useState("");
    const [inverterMakeModel, setInverterMakeModel] = useState("");
    const [inverterCapacity, setInverterCapacity] = useState("");
    const [unitCharges, setUnitCharges] = useState("");
    const [type, setType] = useState("0");

    const [requiredArea, setRequiredArea] = useState(0);
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    // Populate when editing
    useEffect(() => {
        if (initialData) {
            setDate(initialData.date?.split("T")[0] || "");
            setCustomerFullName(initialData.customerFullName || "");
            setSanctionCapacity(initialData.sanctionCapacity || "");
            setAmount(initialData.amount?.toString() || "");
            setModuleMake(initialData.moduleMake || "");
            setModuleCount(initialData.moduleCount || "");
            setWattagePerModule(initialData.wattagePerModule || "");
            setInverterMakeModel(initialData.inverterMakeModel || "");
            setInverterCapacity(initialData.inverterCapacity || "");
            setUnitCharges(initialData.unitCharges || "");
            setType(initialData.type || "0");
        }
    }, [initialData]);

    // Auto calculate required area
    useEffect(() => {
        if (sanctionCapacity) {
            setRequiredArea(Number(sanctionCapacity) * 100);
        } else {
            setRequiredArea(0);
        }
    }, [sanctionCapacity]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (type === "0") {
            toast.error("Please select a Type");
            return;
        }

        const payload = {
            date: date || new Date().toISOString(),
            customerFullName,
            sanctionCapacity,
            amount: parseFloat(amount),
            moduleMake,
            moduleCount,
            wattagePerModule,
            inverterMakeModel,
            inverterCapacity,
            unitCharges,
            type,
        };

        try {
            setLoading(true);
            debugger;
            let response;
            if (initialData?.id) {
                response = await updateQuotation(initialData.id, payload);
            } else {
                response = await createQuotation(payload);
            }

            if (response?.success) {
                toast.success(response.message || "Quotation saved successfully ✅");
                onClose?.(); // optional modal close
                if (typeof onAddQuotation === "function") {
                    onAddQuotation(); // refresh list + navigate handled by parent
                }
            } else {
                toast.error(response?.message || "Failed to save quotation ❌");
            }

        } catch (error) {
            toast.error(error.response?.data?.message || "An error occurred while saving quotation.");
            console.error("Quotation save error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex justify-center">
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-5xl grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-6 rounded-lg shadow-md"
            >
                {/* Date */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Date</label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                    />
                </div>

                {/* Customer */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Customer Full Name</label>
                    <input
                        type="text"
                        value={customerFullName}
                        onChange={(e) => setCustomerFullName(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Enter full name"
                    />
                </div>

                {/* Sanction Capacity */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Sanction Capacity (KW)</label>
                    <input
                        type="number"
                        value={sanctionCapacity}
                        onChange={(e) => setSanctionCapacity(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Sanction capacity (KW)"
                    />
                    {sanctionCapacity && (
                        <p className="text-sm mt-2">
                            <span className="bg-blue-100 text-blue-800 font-semibold px-2 py-1 rounded">
                                Required Area:
                            </span>{" "}
                            {requiredArea} sqft
                        </p>
                    )}
                </div>

                {/* Amount */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Amount (₹)</label>
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Amount"
                    />
                </div>

                {/* Module Make */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Module Make</label>
                    <input
                        type="text"
                        value={moduleMake}
                        onChange={(e) => setModuleMake(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Module make"
                    />
                </div>

                {/* Module Count */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Module Count</label>
                    <input
                        type="number"
                        value={moduleCount}
                        onChange={(e) => setModuleCount(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Module count"
                    />
                </div>

                {/* Wattage per Module */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Wattage per Module</label>
                    <input
                        type="number"
                        value={wattagePerModule}
                        onChange={(e) => setWattagePerModule(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Wattage per module"
                    />
                </div>

                {/* Inverter Make & Model */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Inverter Make & Model</label>
                    <input
                        type="text"
                        value={inverterMakeModel}
                        onChange={(e) => setInverterMakeModel(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Inverter make & model"
                    />
                </div>

                {/* Inverter Capacity */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Inverter Capacity (kVA)</label>
                    <input
                        type="number"
                        value={inverterCapacity}
                        onChange={(e) => setInverterCapacity(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Inverter capacity"
                    />
                </div>

                {/* Unit Charges */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Unit Charges</label>
                    <input
                        type="number"
                        value={unitCharges}
                        onChange={(e) => setUnitCharges(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                        placeholder="Unit charges"
                    />
                </div>

                {/* Type */}
                <div>
                    <label className="mb-1 text-sm font-medium text-gray-700">Type</label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="border rounded px-3 py-2 w-full"
                        required
                    >
                        <option value="0">Select</option>
                        <option value="Regular">Regular</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Society">Society</option>
                    </select>
                </div>

                {/* Buttons */}
                <div className="sm:col-span-2 flex justify-center gap-4 mt-6">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-blue-600 hover:bg-sky-700 text-white font-medium px-5 py-2 text-sm rounded-md shadow-sm transition disabled:opacity-50"
                    >
                        {loading ? "Saving..." : "Submit"}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-200 hover:bg-gray-300 text-black font-medium px-5 py-2 text-sm rounded-md shadow-sm transition"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}

export default QuotationForm;
